import React, { useContext } from 'react';
import CompanySectionLayout from '../../../../component/B2B/CompanySectionLayout';
import { SidebarContext } from '../../../../context/SidebarContext';
import { getLocalStorageItem } from '../../../../utils/helper';
import { GAME_PATHS } from './gamesTheme';

/**
 * Company panel → Games.
 *
 *   Overview     who plays anything, and how broadly across the four
 *   Chess        finished games — results, modes, the daily puzzle
 *   Word Search  levels cleared, scores, hint use by difficulty
 *   Block Fill   levels cleared, time taken, where people have got to
 *   You Quiz     attempts, accuracy, and which quiz modes hold people
 *   Employees    who plays and how much
 *
 * A tab per game rather than one merged page, because the four games record
 * completely different events: a chess game, a word-search level, a block-fill
 * level and a quiz attempt are not the same thing and cannot honestly share a
 * chart axis. The Overview compares them only on the two measures that DO mean
 * the same everywhere — people, and days played.
 *
 * The Employees tab is engagement only, and there is no page behind a row. No
 * score, win rate, rank or level ever appears beside a name, and there is no
 * leaderboard anywhere in this section: the app has its own, which players see
 * and can opt out of, and republishing that ranking under the employer's logo
 * is a different thing from letting people compete with each other.
 */
const CompanyGamesLayout = ({ children }) => {
  const { isB2BFansMode } = useContext(SidebarContext);
  const userData = JSON.parse(getLocalStorageItem('userData') || '{}');
  const slug = userData?.slug;

  const population = isB2BFansMode ? 'members' : 'employees';
  const base = `/${slug}/games`;

  const pages = [
    {
      label: 'Overview',
      path: base,
      description: 'Take-up across all four games, and how broadly people range'
    },
    {
      label: 'Chess',
      path: `${base}/${GAME_PATHS.chess}`,
      description: 'Finished games, how they ended, and the daily puzzle'
    },
    {
      label: 'Word Search',
      path: `${base}/${GAME_PATHS.wordSearch}`,
      description: 'Levels cleared, scores, and how much the hints get used'
    },
    {
      label: 'Block Fill',
      path: `${base}/${GAME_PATHS.blockfill}`,
      description: 'Levels cleared, time taken, and where people have got to'
    },
    {
      label: 'You Quiz',
      path: `${base}/${GAME_PATHS.quiz}`,
      description: 'Attempts, accuracy, and which quiz modes hold people'
    },
    {
      label: isB2BFansMode ? 'Members' : 'Employees',
      path: `${base}/users`,
      description: 'Who plays and how much — never how well'
    }
  ];

  return (
    <CompanySectionLayout
      title="Games"
      subtitle={`How your ${population} use Shoorah's four games. Each game is reported on its own terms — the four record different kinds of play and do not share a unit. Aggregate figures only: there is no leaderboard here, no score or rank appears beside a name, and groups too small to anonymise are not reported at all.`}
      pages={pages}
    >
      {children}
    </CompanySectionLayout>
  );
};

export default CompanyGamesLayout;
